import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { EmailRecord, User } from '../../types/types';

// Query response types
export interface GetUserResponse { userFromUserName: User }
export interface GetUsersResponse { getUsers: User[] }
export interface GetEmailsResponse { getEmailsFromUserName: EmailRecord[] }
export interface GetPhonesResponse { getPhonesFromUserName: { UserName: string; Phone: string; Verified: boolean }[] }
export interface GetUserEmailsForOrgResponse { getUserEmailsForOrg: EmailRecord[] }
export interface ForgotUsernameResponse { forgotUsername: { Email: string } }

const addEmailMutation = gql`
    mutation addEmail($UserName: String!, $Email: String!) {
        addEmail(UserName: $UserName, Email: $Email) {
            UserName
            Email
        }
    }
`;

const deleteEmailMutation = gql`
    mutation deleteEmail(
        $UserName: String!,
        $Email: String!) {
        deleteEmail(
            UserName: $UserName,
            Email: $Email) {
            UserName
            Email
        }
    }
`;

const deactivateEmailMutation = gql`
    mutation deactivateEmail($UserName: String!, $Email: String!) {
        deactivateEmail(UserName: $UserName, Email: $Email) {
            UserName
            Email
        }
    }
`;

const deleteUserMutation = gql`
    mutation deleteUser($UserName: String!) {
        deleteUser(UserName: $UserName) {
            UserName
        }
    }
`;

const updateUserEmailMutation = gql`
    mutation updateUserEmail(
        $UserName: String!,
        $Email: String!,
    ) {
        updateUserEmail(
            UserName: $UserName,
            Email: $Email,
        ) {
            UserName
            Email
        }
    }
`;

const updateUserNameMutation = gql`
    mutation updateUserName(
        $UserName: String!,
        $GivenName: String!,
        $FamilyName: String!,
    ) {
        updateUserName(
            UserName: $UserName,
            GivenName: $GivenName,
            FamilyName: $FamilyName,
        ) {
            UserName
            GivenName
            FamilyName
        }
    }
`;

const updateUserOptOutMutation = gql`
    mutation updateUserOptOut(
        $UserName: String!,
        $EmailOptOut: Boolean!,
    ) {
        updateUserOptOut(
            UserName: $UserName,
            EmailOptOut: $EmailOptOut,
        ) {
            UserName
            EmailOptOut
        }
    }
`;

const verifyEmailMutation = gql`
    mutation verifyEmail($UserName: String!, $Email: String!) {
        verifyEmail(UserName: $UserName, Email: $Email) {
            UserName
            Email
        }
    }
`;

const emailOptOutMutation = gql`
    mutation addEmailOptOut($Email: String!) {
        addEmailOptOut(Email: $Email) {
            Email
        }
    }
`;

const userQuery = gql`
    query getUser(
        $UserName: String!) {
        userFromUserName(
            UserName: $UserName) {
            UserName
            GivenName
            FamilyName
            Email
            EmailOptOut
            Created
            Phone
        }
    }
`;

function useUserQuery(userName: string) {
    return useQuery<GetUserResponse>(
        userQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const getUsersQuery = gql`
    query getUsers(
        $UserName: String!) {
        getUsers(
            UserName: $UserName) {
            UserName
            GivenName
            FamilyName
            Email
            EmailOptOut
            Created
            Phone
        }
    }`;

function useGetUsersQuery(userName: string) {
    return useQuery<GetUsersResponse>(
        getUsersQuery, {
            variables: {
                UserName: userName,
            },
        },
    );
}

const emailQuery = gql`
    query getEmailsFromUserName(
        $UserName: String!) {
        getEmailsFromUserName(UserName: $UserName) {
            UserName
            Email
            Verified
            Active
        }
    }
`;

function useEmailQuery(userName: string, pollInterval: number, skip = false) {
    return useQuery<GetEmailsResponse>(
        emailQuery,
        {
            variables: {
                UserName: userName,
            },
            pollInterval: pollInterval || 0,
            skip,
        },
    );
}

const phoneQuery = gql`
    query getPhonesFromUserName(
        $UserName: String!) {
        getPhonesFromUserName(UserName: $UserName) {
            UserName
            Phone
            Verified
        }
    }
`;

function usePhoneQuery(userName: string) {
    return useQuery<GetPhonesResponse>(
        phoneQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const getUserEmailsForOrgQuery = gql`
    query getUserEmailsForOrg(
        $OrganizationId: String!) {
        getUserEmailsForOrg(
            OrganizationId: $OrganizationId) {
            UserName
            Email
            Active
            User {
                UserName
                GivenName
                FamilyName
            }
        }
    }`;

function useGetUserEmailsForOrgQuery(organizationId: string) {
    return useQuery<GetUserEmailsForOrgResponse>(
        getUserEmailsForOrgQuery, {
            variables: {
                OrganizationId: organizationId,
            },
        },
    );
}

const forgotUsernameQuery = gql`
    query forgotUsername(
        $Email: String!) {
        forgotUsername(Email: $Email) {
            Email
        }
    }
`;

export {
    userQuery,
    getUsersQuery,
    emailQuery,
    phoneQuery,
    getUserEmailsForOrgQuery,
    forgotUsernameQuery,
    addEmailMutation,
    deleteEmailMutation,
    deactivateEmailMutation,
    deleteUserMutation,
    updateUserEmailMutation,
    updateUserNameMutation,
    updateUserOptOutMutation,
    verifyEmailMutation,
    emailOptOutMutation,
    useUserQuery,
    useGetUsersQuery,
    useEmailQuery,
    usePhoneQuery,
    useGetUserEmailsForOrgQuery,
};
